import React, { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

function Dialog({ open, onOpenChange, children }) {
  const handleEscape = useCallback(
    (e) => {
      if (e.key === 'Escape' && onOpenChange) {
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, handleEscape]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      {children}
    </div>,
    document.body
  );
}

function DialogOverlay({ className, onClick, ...props }) {
  return (
    <div
      className={cn(
        'fixed inset-0 bg-black/60 backdrop-blur-sm animate-fade-in',
        className
      )}
      onClick={onClick}
      {...props}
    />
  );
}

function DialogContent({ className, children, onClose, ...props }) {
  const contentRef = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    const content = contentRef.current;
    const selector = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]';
    const focusable = () => Array.from(content?.querySelectorAll(selector) || []).filter(el => !el.closest('[hidden]'));
    const initial = content?.querySelector('input') || focusable()[0];
    initial?.focus();
    const trap = event => {
      if (event.key !== 'Tab') return;
      const elements = focusable();
      if (!elements.length) { event.preventDefault(); return; }
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && (document.activeElement === first || !content.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !content.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    content?.addEventListener('keydown', trap);
    return () => { content?.removeEventListener('keydown', trap); if (previous?.isConnected) previous.focus(); };
  }, []);
  return (
    <>
      <DialogOverlay onClick={onClose} />
      <div
        ref={contentRef} role="dialog" aria-modal="true"
        className={cn(
          'fixed left-1/2 top-1/2 z-50 grid w-[calc(100%-2rem)] max-w-lg max-h-[90dvh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 gap-4 border border-border bg-card p-6 shadow-lg rounded-lg animate-fade-in',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </>
  );
}

function DialogHeader({ className, ...props }) {
  return (
    <div
      className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, children, ...props }) {
  return (
    <h2
      className={cn('text-lg font-semibold leading-none tracking-tight text-foreground', className)}
      {...props}
    >{children}</h2>
  );
}

function DialogDescription({ className, ...props }) {
  return (
    <p
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }) {
  return (
    <div
      className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
};
